import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Stethoscope } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { TUMOR_GUIDE, TREATMENTS } from "../content/siteContent";
import { locT, locTumor } from "../content/i18n_extra";

export function TumorGuide() {
  const { t, lang } = useLang();
  const [selected, setSelected] = useState(null);

  const active = TUMOR_GUIDE.find((g) => g.id === selected);
  const recommended = active ? active.treatments.map((id) => TREATMENTS.find((x) => x.id === id)).filter(Boolean) : [];

  return (
    <section id="rehber" className="bg-ice py-28 sm:py-36" data-testid="tumor-guide-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="max-w-3xl">
          <div className="text-xs font-heading font-bold tracking-[0.3em] uppercase text-brand mb-4">{t("sec_guide_kicker")}</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-ink text-balance">{t("sec_guide_title")}</h2>
          <p className="mt-5 text-lg text-sub leading-relaxed">{t("sec_guide_sub")}</p>
        </motion.div>

        <div className="mt-12 flex flex-wrap gap-2.5" data-testid="tumor-chips">
          {TUMOR_GUIDE.map((g) => (
            <button
              key={g.id}
              data-testid={`tumor-chip-${g.id}`}
              onClick={() => setSelected(selected === g.id ? null : g.id)}
              className={`px-4 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 border ${selected === g.id ? "bg-brand text-white border-brand shadow-lg shadow-brand/25" : "bg-white text-ink/70 border-black/10 hover:border-brand/40 hover:text-brand"}`}
            >
              {locTumor(g, lang)}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {active && (
            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
              className="mt-8 bg-white rounded-3xl border border-brand/15 p-7 sm:p-9 shadow-xl shadow-brand/5"
              data-testid="tumor-result"
            >
              <div className="flex items-center gap-3 mb-5">
                <span className="w-10 h-10 rounded-2xl bg-brand/10 text-brand flex items-center justify-center"><Stethoscope size={18} /></span>
                <div>
                  <div className="font-heading font-bold text-ink">{locTumor(active, lang)}</div>
                  <div className="text-xs text-sub">{t("guide_recommended")}</div>
                </div>
                <Link to={`/kanser/${active.id}`} data-testid={`tumor-page-link-${active.id}`}
                  className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold text-brand hover:text-branddeep transition-colors">
                  {t("tp_kicker")} <ArrowRight size={12} />
                </Link>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {recommended.map((tr) => (
                  <Link
                    key={tr.id}
                    to={`/tedavi/${tr.id}`}
                    data-testid={`tumor-rec-${tr.id}`}
                    className="text-left rounded-2xl border border-black/5 bg-[#F9FBFD] p-4 hover:border-brand/30 hover:shadow-md transition-all duration-200 group block"
                  >
                    <span className="inline-block w-2 h-2 rounded-full mb-2.5" style={{ backgroundColor: tr.accent }} />
                    <div className="text-sm font-bold text-ink leading-snug">{locT(tr, lang).title}</div>
                    <div className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-brand">
                      {t("guide_details")} <ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </Link>
                ))}
              </div>
              <p className="mt-5 text-[11px] text-sub leading-relaxed">{t("guide_disclaimer")}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
